import type { FC, SVGProps } from 'react';
import { useState } from 'react';

import { IconButton, Typography } from '@mui/material';
import { QuestionIcon } from '@truvhq/ui';
import { useLocation } from 'react-router-dom';

import { SandboxSwitch } from 'components/SandboxSwitch/SandboxSwitch';
import { useAssistantEnabled } from 'features/onboarding/lib/useAssistantEnabled';
import { useOnboardingDialogStore } from 'features/onboarding/lib/useOnboardingDialogStore';
import { TenantCompanies } from 'features/tenancy/ui/TenantCompanies';
import { useGetTenantCompanies } from 'hooks/company';
import { useAuth } from 'shared/lib/auth/useAuth';
import { useSandboxContext } from 'shared/lib/sandbox/useSandboxContext';
import { useTenancyContext } from 'shared/lib/tenancy/useTenancyContext';
import DoubleChevronIcon from 'shared/ui/icons/double-chevron.svg?react';
import LogoIcon from 'shared/ui/icons/logo.svg?react';
import { urls } from 'shared/urls';

import { NavItems } from './NavItems';
import type { Item, State } from './types';

const iconProps: SVGProps<SVGSVGElement> = {
  width: 16,
  height: 16,
};

const expandAll = (items: Item[]): State =>
  Object.fromEntries(items.filter((item) => item.href && item.submenu).map((item) => [item.href, true]));

type MobileNavProps = {
  items: Item[];
  onClose: VoidFunction;
};

export const MobileNav: FC<MobileNavProps> = ({ items, onClose }) => {
  const { pathname } = useLocation();
  const { permissions } = useAuth();
  const { isSandboxVisible } = useSandboxContext();
  const { data: tenantCompanies } = useGetTenantCompanies();
  const { tenantCompanyId, setTenantCompanyId } = useTenancyContext();
  const assistantEnabled = useAssistantEnabled();
  const openAssistant = useOnboardingDialogStore((state) => state.open);
  // A phone has no room to hunt for a section, so every submenu starts open. Kept apart from the
  // desktop's shared submenu state so collapsing one here doesn't collapse it on the sidebar.
  const [submenuState, setSubmenuState] = useState(() => expandAll(items));

  // The drawer is the assistant's door on a phone: the desktop's floating pill would sit on top of
  // whatever the page pins to its bottom edge (CL-6904). It stands in for support — the assistant
  // answers the same questions, and without leaving the tab — but right under Home, where it is the
  // first thing the thumb finds rather than the last row of a long list.
  const assistantItem: Item = {
    title: 'Ask Truv AI',
    icon: <QuestionIcon {...iconProps} />,
    customAction() {
      openAssistant();
      onClose();
    },
  };
  const shownItems = assistantEnabled
    ? items
        .filter((item) => item.title !== urls.supportTickets.title)
        .flatMap((item) => (item.title === urls.home.title ? [item, assistantItem] : [item]))
    : items;

  return (
    <div className="relative flex h-full w-[300px] max-w-[85vw] flex-col bg-grey100" data-testid="mobile-nav">
      <div className="flex h-full flex-col overflow-y-auto">
        <div className="flex items-center px-6 pt-5 pb-8">
          <LogoIcon className="h-9 w-auto text-white" />
        </div>

        <NavItems
          items={shownItems}
          pathname={pathname}
          permissions={permissions}
          setSubmenuState={setSubmenuState}
          submenuState={submenuState}
          // Closed here rather than left to the route change: every route renders its own layout,
          // so navigating unmounts this drawer and mounts another one, which would come up open —
          // two overlays across one commit, and the second one's backdrop can outlive its transition.
          // Only links close it: Log out's confirm dialog belongs to NavBar and dies with the drawer.
          onNavigate={onClose}
        />

        <div className="mt-auto flex flex-col gap-4 px-4 pt-6">
          <div className="rounded-lg border border-grey70 p-4">
            <Typography className="text-white" variant="h3">
              Mobile dashboard is limited
            </Typography>
            <Typography className="mt-1 text-grey50" variant="body2">
              Switch to desktop for the full experience
            </Typography>
          </div>
        </div>

        <div className="mt-4 flex flex-col bg-grey90">
          {tenantCompanies && tenantCompanies.length > 1 && (
            <div className="flex h-[52px] flex-col justify-center">
              <TenantCompanies
                disableTooltip
                selectedCompanyId={tenantCompanyId}
                tenantCompanies={tenantCompanies}
                onSelectCompany={setTenantCompanyId}
              />
            </div>
          )}
          {isSandboxVisible && (
            <div className="flex h-[54px] flex-col justify-center border-white/10 border-t-2">
              <div className="px-4">
                <SandboxSwitch invert />
              </div>
            </div>
          )}
        </div>
      </div>

      <IconButton
        aria-label="Close navigation"
        className="-translate-y-1/2 absolute top-[30px] right-0 h-8 w-8 translate-x-1/2 bg-grey80 p-0 text-white hover:bg-grey70"
        data-testid="mobile-nav-close"
        onClick={onClose}
      >
        <DoubleChevronIcon className="h-5 w-5 rotate-180" />
      </IconButton>
    </div>
  );
};
